<template>
  <!-- Desktop Sidebar -->
  <Transition name="notifications-fade">
    <div v-if="!isMobileView && openNotice" class="desktop-notifications-overlay" @click="onClose">
      <Transition name="notifications-slide">
        <div v-if="!isMobileView && openNotice" class="desktop-notifications-sidebar" @click.stop>
          <div class="modal__top">
            <my-button-circle class="btn-circle_grey" :icon="'close'" @click="onClose" />
          </div>

          <div class="modal__content">
            <div class="notice-info">
              <div class="notice-info__top">
                <div class="notice-info__title">{{ trans('Notifications') }}</div>

                <div class="notice-info__status notice-info__status_green" @click="handleMarkAllAsRead">
                  <svg class="icon ic-check" width="17" height="9">
                    <use xlink:href="@/assets/sprites/sprite.svg#ic-check"></use>
                  </svg>
                  {{ trans('Mark all as read') }}
                </div>
              </div>

              <div class="tabs-page tabs-page_line">
                <div class="tabs-page__header-wrapper">
                  <TabHeader v-model="activeTab" :tabs="tabs" />
                </div>

                <div class="tabs-page__content-wrapper">
                  <TabContent v-if="activeTab === 'all'" :is-active="true" />
                  <TabContent
                    v-else-if="activeTab === 'unread'"
                    :read-filter="false"
                    :is-active="true"
                    @refresh-count="fetchNotificationsCount"
                  />
                  <TabContent
                    v-else-if="activeTab === 'read'"
                    :read-filter="true"
                    :is-active="true"
                    @refresh-count="fetchNotificationsCount"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>

  <!-- Mobile Notifications with sliding effect -->
  <Mob_Notifications v-if="isMobileView" :open-notice="openNotice" @close="onClose" />
</template>
<script setup lang="ts">
import { trans } from '@/common/language/translations';

import { computed, onMounted, ref } from 'vue';
import MyButtonCircle from '../UI/MyButtonCircle.vue';
import TabHeader from '@/components/Notifications/TabHeader.vue';
import TabContent from '@/components/Notifications/TabContent.vue';
import axios from '@/common/lib/axios';
import { useNotificationStore } from '@/store/notification';
import Mob_Notifications from '@/mobile_components/Mob_Notifications.vue';
import { useUIStore } from '@/store/ui';
import { User_URL } from '@/common/lib/url';

export interface NotificationTabHeader {
  id: string;
  label: string;
  count: number;
  countClass?: string;
}

interface NotificationsCountResponse {
  success: boolean;
  result: {
    read: number;
    unread: number;
  };
}

defineProps({
  openNotice: {
    type: Boolean,
    default: false,
    required: true
  }
});
const emit = defineEmits(['close', 'allNotificationsRead']);

const notificationStore = useNotificationStore();
const uiStore = useUIStore();
const activeTab = ref('all');

const isMobileView = computed(() => uiStore.isMobile);
const readCount = computed(() => notificationStore.getReadCount);
const unreadCount = computed(() => notificationStore.getUnreadCount);
const allCount = computed(() => notificationStore.getAllCount);

const tabs = computed<NotificationTabHeader[]>(() => [
  {
    id: 'all',
    label: trans('All'),
    count: allCount.value,
    countClass: 'tabs-page__count_red'
  },
  {
    id: 'unread',
    label: trans('Unread'),
    count: unreadCount.value
  },
  {
    id: 'read',
    label: trans('Read'),
    count: readCount.value
  }
]);

const fetchNotificationsCount = async () => {
  try {
    const response = await axios.get<NotificationsCountResponse>(User_URL + 'notifications/count');
    if (response.data.success) {
      const notificationStore = useNotificationStore();
      notificationStore.setReadCount(response.data.result.read);
      notificationStore.setUnreadCount(response.data.result.unread);
    }
  } catch (error) {
    console.error('An error occurred while fetching notifications count:', error);
  }
};

const setupNotificationFetching = () => {
  fetchNotificationsCount();
  setInterval(fetchNotificationsCount, 600000); // Fetch every 10 minutes (600000ms)
};

onMounted(() => {
  setupNotificationFetching();
});

const handleMarkAllAsRead = async () => {
  await notificationStore.markAllAsRead();
};

const onClose = () => {
  activeTab.value = 'all';
  emit('close');
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/tabs-page.scss';

// Desktop Sidebar Styles (like sidebar but with original content styling)
.desktop-notifications-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.desktop-notifications-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: white;
  box-shadow: -5px 0 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal__top {
  display: flex;
  justify-content: flex-end;
  padding: 20px 20px 0 20px;
  flex-shrink: 0;
}

.modal__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 20px 20px 20px;
}

.notice-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #eee;
    position: sticky;
    top: 0;
    background: white;
    z-index: 10;
    padding-top: 20px;
    flex-shrink: 0;
  }

  &__title {
    font-size: 24px;
    font-weight: 600;
    color: #1F242C;
  }

  &__status {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 14px;
    font-weight: 500;

    &_green {
      background: #E8F5E8;
      color: #2E7D32;
      border: 1px solid #C8E6C9;

      &:hover {
        background: #C8E6C9;
      }
    }
  }
}

.tabs-page {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  &__header-wrapper {
    flex-shrink: 0;
    position: sticky;
    top: 75px; // Position below notice-info__top (approx height: 20px padding-top + ~35px content + 15px padding-bottom + 5px margin)
    background: white;
    z-index: 9;
    
    :deep(.tabs-page__list) {
      margin-bottom: 0;
      padding-bottom: 0;
    }
  }

  &__content-wrapper {
    flex: 1;
    min-height: 0;
  }
}

// Vue Transition Styles
.notifications-fade-enter-active,
.notifications-fade-leave-active {
  transition: opacity 0.3s ease;
}

.notifications-fade-enter-from,
.notifications-fade-leave-to {
  opacity: 0;
}

.notifications-slide-enter-active,
.notifications-slide-leave-active {
  transition: transform 0.3s ease;
}

.notifications-slide-enter-from,
.notifications-slide-leave-to {
  transform: translateX(100%);
}

// Legacy Animations (for fallback)
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

// Mobile-specific styles (only for mobile modal)
@media only screen and (max-width: 991px) {
  .desktop-notifications-overlay {
    display: none;
  }
}

// Desktop-specific styles (only for desktop sidebar)
@media only screen and (min-width: 992px) {
  .desktop-notifications-overlay {
    display: block;
  }
}
</style>
